/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { Col, Row } from "react-awesome-styled-grid";
import { Heading, Text, theme } from "@nordcloud/gnui";
import { Resource } from "~/generated/graphql";
import { Item } from "~/types";
import { ResourceSelect } from "~/views/resourceGroups/components/Resource";
import { Props, ResourceGroupField } from "../constants";

export function ResourceSelectForm({
  onSubmit,
  onDynamicSubmit,
  setResourceGroupData,
  resourceGroupData,
}: Props) {
  const [selectedResources, setSelectedResources] = useState<string[]>(
    resourceGroupData[ResourceGroupField.MANUAL_SELECTED_RESOURCES]
  );

  const [dynamicResources, setDynamicResources] = useState<Item[]>(
    resourceGroupData?.[ResourceGroupField.DYNAMIC_RESOURCES] ?? []
  );

  const [allResources, setAllResources] = useState<Resource[]>(
    resourceGroupData[ResourceGroupField.ALL_RESOURCES] ?? []
  );

  const [expression, setExpression] = useState<string | undefined>(
    resourceGroupData.expression
  );

  const handleFormSubmit = () => {
    const formData = {
      ...resourceGroupData,
      [ResourceGroupField.MANUAL_SELECTED_RESOURCES]: selectedResources,
      [ResourceGroupField.ALL_RESOURCES]: allResources,
    };
    onSubmit(formData);
  };

  const handleReorder = (ids: string[]) => {
    setSelectedResources(ids);
  };

  useEffect(() => {
    setResourceGroupData((prevResourceData) => {
      return {
        ...prevResourceData,
        [ResourceGroupField.MANUAL_SELECTED_RESOURCES]: selectedResources,
        [ResourceGroupField.DYNAMIC_RESOURCES]: dynamicResources,
        [ResourceGroupField.ALL_RESOURCES]: allResources,
        [ResourceGroupField.EXPRESSION]: expression,
      };
    });
  }, [
    allResources,
    dynamicResources,
    selectedResources,
    setResourceGroupData,
    expression,
  ]);

  return (
    <>
      <Row justify="center">
        <Col sm={6} md={6} lg={8}>
          <Heading level={3}>Add resources to resource group</Heading>
          <Text size="sm" mb={theme.spacing.spacing04}>
            Add resources manually together with expressions. Resources will be
            executed in the order they appear on the list. You can drag and drop
            resources however you want.
          </Text>

          <ResourceSelect
            {...{
              selectedResources,
              setSelectedResources,
              dynamicResources,
              setDynamicResources,
              allResources,
              setAllResources,
              resourceGroupData,
              handleReorder,
              setExpression,
            }}
            onSubmit={handleFormSubmit}
            onDynamicSubmit={onDynamicSubmit}
          />
        </Col>
      </Row>
    </>
  );
}
